import { useSeo } from '../lib/seo';
/**
 * Public /awards/tickets — Awards ceremony ticket booking.
 *
 * Sits OUTSIDE the trade-partner paywall so anyone can discover the ceremony
 * and see pricing. Buying a ticket requires a trade-partner login (per user
 * spec) — sign-in / apply forms are inline below the ticket cards.
 *
 * Two flat rates: non-partners pay the standard price; ANY signed-in trade
 * partner pays the partner rate, regardless of paid membership level.
 */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Award, Calendar, Check, LogIn, LogOut, MapPin, Ticket, UserPlus, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import partnerApi, { getPartnerToken, setPartnerSession, clearPartnerSession } from '../lib/partnerApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = {
  nonmember: 'Standard',
  partner: 'Member (Bronze / Silver / Gold)',
};

const TIER_BADGE_COLOR = {
  nonmember: 'bg-charcoal/10 text-charcoal',
  partner: 'bg-forest/10 text-forest',
};

function money(pounds, currency = 'gbp') {
  if (pounds == null) return '—';
  const symbol = currency === 'gbp' ? '£' : currency.toUpperCase() + ' ';
  return `${symbol}${Number(pounds).toFixed(2)}`;
}

export default function AwardsTicketsPage() {
  useSeo(
    'Tourism Awards Tickets & Tables | Go Leicestershire',
    'Book your seats or a table for the Leicestershire Tourism Awards night — partner and non-partner pricing available.'
  );
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [partner, setPartner] = useState(null);      // {membership_tier, business_name, email}
  const [checkingSession, setCheckingSession] = useState(true);
  const [buyTicket, setBuyTicket] = useState(null);  // currently-selected ticket for the buy modal
  const [avail, setAvail] = useState(null);          // {capacity, seats_remaining, sold_out}
  const [waitlistOpen, setWaitlistOpen] = useState(null); // null | {tickets, name, email} prefill

  // 1) Check if we have a valid partner session on mount
  useEffect(() => {
    if (!getPartnerToken()) { setCheckingSession(false); return; }
    partnerApi.get('/partners/me')
      .then(({ data }) => setPartner(data))
      .catch(() => { clearPartnerSession(); setPartner(null); })
      .finally(() => setCheckingSession(false));
  }, []);

  // 2) Load ticket catalog
  useEffect(() => {
    setLoading(true); setErr('');
    axios.get(`${API}/payments/products?category=ticket`)
      .then(({ data }) => setTickets(data.items || []))
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load tickets'))
      .finally(() => setLoading(false));
    axios.get(`${API}/payments/tickets/availability`)
      .then(({ data }) => setAvail(data))
      .catch(() => setAvail(null));
  }, []);

  const PAID_TIERS = ['bronze', 'silver', 'gold'];
  const audienceTier = partner && PAID_TIERS.includes((partner.membership_tier || '').toLowerCase()) ? 'partner' : 'nonmember';
  const isSignedIn = !!partner;

  function onBuyClick(ticket) {
    // Everyone can buy — guests pay the standard rate; paid members the member rate.
    setBuyTicket(ticket);
  }

  function signOut() {
    clearPartnerSession();
    setPartner(null);
  }

  return (
    <div className="bg-base min-h-screen">
      <Helmet>
        <title>Tourism Awards 2026 tickets — GoLeicestershire</title>
        <meta name="description" content="Book your seat at the 2026 Leicestershire Tourism Awards ceremony — Thursday 26 November, National Space Centre." />
      </Helmet>

      {/* Hero */}
      <section className="bg-heritage text-base py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative">
          <div className="label text-mustard mb-3 flex items-center gap-2"><Award size={14}/> Leicestershire Tourism Awards 2026</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-4">
            Book your <em className="not-italic text-mustard">ceremony seats</em>
          </h1>
          <p className="text-base/85 text-lg max-w-2xl leading-relaxed">
            Join Leicestershire&apos;s tourism community for a black-tie evening celebrating the county&apos;s
            best venues, teams and experiences.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-base/80">
            <div className="flex items-center gap-2"><Calendar size={14}/> Thursday 26 November 2026</div>
            <div className="flex items-center gap-2"><MapPin size={14}/> National Space Centre, Leicester</div>
          </div>
        </div>
      </section>

      {/* Signed-in status strip */}
      {isSignedIn && (
        <div className="bg-forest/5 border-b border-forest/20" data-testid="signed-in-strip">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-3 flex flex-wrap gap-3 items-center justify-between text-sm">
            <div className="flex items-center gap-3">
              <Check className="text-forest" size={16}/>
              <span>Signed in as <b>{partner.business_name || partner.email}</b></span>
              <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${TIER_BADGE_COLOR[audienceTier]}`}>
                {TIER_LABEL[audienceTier]} pricing
              </span>
            </div>
            <button onClick={signOut} data-testid="awards-tickets-signout-btn"
              className="text-charcoal/60 hover:text-heritage flex items-center gap-1 text-xs">
              <LogOut size={12}/> Sign out
            </button>
          </div>
        </div>
      )}

      {/* Tickets */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12 md:py-16">
        {loading && <div className="text-charcoal/60 flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading tickets…</div>}
        {err && <div data-testid="tickets-error" className="p-4 rounded-xl bg-heritage/10 text-heritage">{err}</div>}
        {!loading && tickets.length === 0 && (
          <div className="p-6 rounded-2xl bg-white border border-charcoal/10 text-charcoal/70">
            Tickets aren&apos;t on sale yet. Please check back soon.
          </div>
        )}

        {tickets.length > 0 && (
          <>
            {avail?.sold_out && (
              <div className="mb-6 p-5 rounded-2xl bg-heritage text-base flex flex-wrap items-center gap-4" data-testid="tickets-soldout-banner">
                <div className="flex-1 min-w-[260px]">
                  <div className="font-display text-xl mb-1">The ceremony is fully booked</div>
                  <div className="text-sm text-base/85">Join the waiting list and we&apos;ll contact you straight away if seats become available.</div>
                </div>
                <button onClick={() => setWaitlistOpen({})} data-testid="tickets-waitlist-open-btn"
                  className="bg-mustard text-charcoal font-semibold px-6 py-2.5 rounded-full text-sm hover:bg-base transition-colors">
                  Join the waiting list
                </button>
              </div>
            )}
            {!avail?.sold_out && avail?.seats_remaining != null && avail.seats_remaining <= 60 && (
              <div className="mb-6 p-4 rounded-xl bg-heritage/10 border border-heritage/30 text-heritage text-sm font-semibold" data-testid="tickets-low-stock-note">
                Nearly sold out — only {avail.seats_remaining} seat{avail.seats_remaining === 1 ? '' : 's'} remain. If you need more than that,{' '}
                <button onClick={() => setWaitlistOpen({})} className="underline" data-testid="tickets-waitlist-inline-btn">join the waiting list</button>.
              </div>
            )}
            <div className="mb-6 p-4 rounded-xl bg-mustard/15 border border-mustard/50 text-charcoal text-sm flex items-start gap-2.5" data-testid="tickets-shared-table-note">
              <span className="font-bold shrink-0">Please note:</span>
              <span>Only bookings of <strong>ten or more seats</strong> are guaranteed an exclusive table. Smaller bookings will be seated on a shared table with other guests. A limited number of <strong>tables of 12</strong> are available on a first come, first served basis — otherwise the maximum table size is <strong>10</strong>. All prices exclude VAT — <strong>20% VAT is added at checkout</strong>.</span>
            </div>
            <div className="mb-6 p-4 rounded-xl bg-stone border border-charcoal/15 text-charcoal/80 text-sm" data-testid="tickets-refund-note">
              <strong>Refund policy:</strong> No refunds are available on awards tickets. However, in the event of an unavoidable situation, we will attempt to resell your tickets on your behalf — if successful, we will refund the amount paid less a 20% administration cost.
            </div>
            <div className="grid md:grid-cols-3 gap-6" data-testid="awards-tickets-grid">
            {tickets.map((t) => {
              const yourPrice = (t.tier_prices || {})[audienceTier] ?? t.amount;
              const nonMemberPrice = (t.tier_prices || {}).nonmember ?? t.amount;
              const savings = isSignedIn ? (nonMemberPrice - yourPrice) : 0;
              return (
                <div key={t.id} data-testid={`ticket-card-${t.lookup_key}`}
                  className="bg-white border border-charcoal/10 rounded-2xl p-6 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Ticket className="text-heritage" size={18}/>
                    <span className="text-[10px] uppercase tracking-wide font-bold text-charcoal/50">{t.ticket_type || 'Ticket'}</span>
                  </div>
                  <div className="font-display text-2xl mb-2">{t.name.replace(/^Awards Ceremony\s—\s/, '')}</div>
                  {t.description && <p className="text-sm text-charcoal/70 mb-4 flex-1">{t.description}</p>}

                  {/* Tier-price ladder */}
                  <div className="border-t border-charcoal/10 pt-4 mb-4 space-y-1 text-xs">
                    {['nonmember', 'partner'].map((tier) => {
                      const p = (t.tier_prices || {})[tier] ?? t.amount;
                      const isMine = isSignedIn && audienceTier === tier;
                      return (
                        <div key={tier} className={`flex justify-between ${isMine ? 'font-semibold text-heritage' : 'text-charcoal/60'}`}>
                          <span>{TIER_LABEL[tier]}{isMine && ' — your price'}</span>
                          <span>{money(p, t.currency)}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Your price + Buy */}
                  <div className="mt-auto">
                    {isSignedIn ? (
                      <div className="mb-3">
                        <div className="text-xs uppercase tracking-wide text-charcoal/50">Your price</div>
                        <div className="font-display text-3xl text-heritage">{money(yourPrice, t.currency)}</div>
                        {savings > 0 && (
                          <div className="text-xs text-forest">You save {money(savings, t.currency)} as a {TIER_LABEL[audienceTier]}</div>
                        )}
                      </div>
                    ) : (
                      <div className="mb-3">
                        <div className="text-xs uppercase tracking-wide text-charcoal/50">Standard price</div>
                        <div className="font-display text-3xl">{money(nonMemberPrice, t.currency)}</div>
                        <div className="text-xs text-charcoal/55">Bronze, Silver &amp; Gold members: sign in below for the member rate.</div>
                      </div>
                    )}
                    <button
                      data-testid={`ticket-buy-${t.lookup_key}`}
                      onClick={() => (avail?.sold_out ? setWaitlistOpen({}) : onBuyClick(t))}
                      className="w-full bg-heritage text-base font-semibold rounded-full py-2.5 text-sm hover:bg-charcoal transition-colors flex items-center justify-center gap-2"
                    >
                      {avail?.sold_out ? 'Join the waiting list' : 'Book now'} <Ticket size={14}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </section>

      {/* Sign-in / apply panel — only when unsigned */}
      {!checkingSession && !isSignedIn && (
        <SignInOrApplyPanel onSignIn={(p) => setPartner(p)} />
      )}

      {/* Buy modal */}
      {buyTicket && (
        <TicketCheckoutModal
          ticket={buyTicket}
          audienceTier={audienceTier}
          partner={partner}
          onClose={() => setBuyTicket(null)}
          onWaitlist={(prefill) => { setBuyTicket(null); setWaitlistOpen(prefill || {}); }}
        />
      )}

      {/* Waiting list modal */}
      {waitlistOpen && (
        <WaitlistModal prefill={waitlistOpen} partner={partner} onClose={() => setWaitlistOpen(null)} />
      )}
    </div>
  );
}

/** WaitlistModal — captures contact details when demand exceeds remaining seats. */
function WaitlistModal({ prefill, partner, onClose }) {
  const [form, setForm] = useState({
    name: prefill.name || partner?.contact_name || '',
    company: prefill.company || partner?.business_name || '',
    tickets: prefill.tickets || 1,
    email: prefill.email || partner?.email || '',
    phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit() {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setErr('Please add your name, email and phone number.'); return;
    }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/payments/tickets/waitlist`, {
        name: form.name.trim(),
        company: form.company.trim(),
        tickets_requested: Math.max(1, Number(form.tickets) || 1),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      setDone(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Could not join the waiting list — please check your details and try again.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="tickets-waitlist-modal">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-charcoal/10 px-6 py-4 flex items-center gap-3">
          <Ticket className="text-heritage" size={20}/>
          <div className="font-display text-xl flex-1">Join the ticket waiting list</div>
          <button data-testid="tickets-waitlist-close" onClick={onClose} className="p-2 rounded-lg hover:bg-admin"><X size={20}/></button>
        </div>
        <div className="p-6">
          {done ? (
            <div className="text-center py-6" data-testid="tickets-waitlist-success">
              <Check className="mx-auto text-forest mb-3" size={36}/>
              <div className="font-display text-xl mb-2">You&apos;re on the list</div>
              <p className="text-sm text-charcoal/70">We&apos;ve emailed you a confirmation. If seats become available we&apos;ll contact you straight away — no payment has been taken.</p>
              <button onClick={onClose} className="mt-5 bg-heritage text-base font-semibold px-6 py-2.5 rounded-full text-sm" data-testid="tickets-waitlist-done-btn">Done</button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-charcoal/70">Demand for the 2026 ceremony is very high. Leave your details and we&apos;ll be in touch the moment seats free up.</p>
              <div>
                <label className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide">Your name *</label>
                <input value={form.name} onChange={set('name')} data-testid="waitlist-name-input" className="mt-1 w-full border border-charcoal/20 rounded-xl px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide">Company name</label>
                <input value={form.company} onChange={set('company')} data-testid="waitlist-company-input" className="mt-1 w-full border border-charcoal/20 rounded-xl px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide">Tickets needed *</label>
                <input type="number" min="1" max="200" value={form.tickets} onChange={set('tickets')} data-testid="waitlist-tickets-input" className="mt-1 w-28 border border-charcoal/20 rounded-xl px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide">Email *</label>
                <input type="email" value={form.email} onChange={set('email')} data-testid="waitlist-email-input" className="mt-1 w-full border border-charcoal/20 rounded-xl px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide">Phone number *</label>
                <input type="tel" value={form.phone} onChange={set('phone')} data-testid="waitlist-phone-input" className="mt-1 w-full border border-charcoal/20 rounded-xl px-3 py-2 text-sm"/>
              </div>
              {err && <div className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm" data-testid="waitlist-error">{err}</div>}
              <button onClick={submit} disabled={busy} data-testid="waitlist-submit-btn"
                className="w-full bg-heritage text-base font-semibold rounded-full py-3 text-sm hover:bg-charcoal transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={16}/> : null} Join the waiting list
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * TicketCheckoutModal — buyer picks quantity + confirms details, then redirects to Stripe.
 * The server will pick the tier price based on the caller's JWT — we display it here for confirmation.
 */
function TicketCheckoutModal({ ticket, audienceTier, partner, onClose, onWaitlist }) {
  const [qty, setQty] = useState(1);
  const maxQty = ticket.quantity_max || 20;
  // qty may be '' while the buyer is retyping — clamp to a real number everywhere it's used
  const qtyNum = Math.max(1, Math.min(maxQty, Number(qty) || 1));
  const [notes, setNotes] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [showAttendees, setShowAttendees] = useState(false);
  const seatsPer = (ticket.ticket_type || '').toLowerCase().includes('table') ? 10 : 1;
  const totalSeats = Math.min(qtyNum * seatsPer, 30);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [soldOut, setSoldOut] = useState(false);
  const unit = (ticket.tier_prices || {})[audienceTier] ?? ticket.amount;
  const total = unit * qtyNum;
  const isGuest = !partner;

  async function checkout() {
    if (isGuest && (!guestName.trim() || !guestEmail.trim())) {
      setErr('Please add your name and email so we can issue your tickets.');
      return;
    }
    setSubmitting(true); setErr('');
    try {
      const { data } = await partnerApi.post('/payments/checkout', {
        lookup_key: ticket.lookup_key,
        quantity: qtyNum,
        origin_url: window.location.origin,
        attendee_names: attendees.map((n) => (n || '').trim()).filter(Boolean),
        buyer_email: partner?.email || guestEmail.trim(),
        buyer_name: partner?.contact_name || partner?.business_name || guestName.trim(),
        notes: notes || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e.response?.data?.detail || '';
      if (typeof d === 'string' && d.startsWith('SOLD_OUT:')) {
        const parts = d.split(':');
        setSoldOut(true);
        setErr(parts.slice(2).join(':'));
      } else {
        setErr(d || 'Checkout failed. Please try again.');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="ticket-checkout-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-charcoal/10 px-6 py-4 flex items-center gap-3">
          <Ticket className="text-heritage" size={22}/>
          <div className="flex-1">
            <div className="font-display text-xl">{ticket.name}</div>
            <div className="text-xs text-charcoal/60">
              <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${TIER_BADGE_COLOR[audienceTier]}`}>
                {TIER_LABEL[audienceTier]} pricing
              </span>
              <span className="ml-2">{money(unit, ticket.currency)} each</span>
            </div>
          </div>
          <button data-testid="ticket-checkout-close" onClick={onClose} className="p-2 rounded-lg hover:bg-admin"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-5">
          {err && (
            <div className="p-3 rounded-xl bg-heritage/10 text-heritage text-sm" data-testid="ticket-checkout-error">
              {err}
              {soldOut && (
                <button onClick={() => onWaitlist({ tickets: qtyNum * seatsPer, name: partner?.contact_name || guestName, company: partner?.business_name || '', email: partner?.email || guestEmail })}
                  data-testid="ticket-checkout-waitlist-btn"
                  className="mt-2 block w-full bg-heritage text-base font-semibold rounded-full py-2.5 text-sm hover:bg-charcoal transition-colors">
                  Join the waiting list instead
                </button>
              )}
            </div>
          )}
          {isGuest && (
            <>
              <label className="block">
                <span className="label">Your name</span>
                <input value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="ticket-guest-name"
                  className="mt-1 w-full border rounded-xl px-3 py-2" placeholder="Full name"/>
              </label>
              <label className="block">
                <span className="label">Email (for your booking confirmation)</span>
                <input type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} data-testid="ticket-guest-email"
                  className="mt-1 w-full border rounded-xl px-3 py-2" placeholder="you@example.com"/>
              </label>
            </>
          )}
          <label className="block">
            <span className="label">Quantity</span>
            <input type="number" min="1" max={maxQty} value={qty}
              onChange={(e) => {
                const raw = e.target.value;
                // let the field go empty while retyping — backspace must work
                setQty(raw === '' ? '' : Math.max(1, Math.min(maxQty, Number(raw) || 1)));
              }}
              onBlur={() => setQty(qtyNum)}
              data-testid="ticket-checkout-qty-input"
              className="mt-1 w-full border rounded-xl px-3 py-2"/>
          </label>
          <div className="border border-charcoal/10 rounded-xl p-3">
            <button type="button" onClick={() => setShowAttendees(!showAttendees)} data-testid="ticket-attendees-toggle"
              className="w-full flex items-center justify-between text-sm font-semibold">
              <span>Guest names for your {totalSeats > 1 ? `${totalSeats} seats` : 'seat'} (optional)</span>
              <span className="text-heritage">{showAttendees ? '−' : '+'}</span>
            </button>
            <p className="text-xs text-charcoal/55 mt-1">
              Adding names now helps us build the table plan — but don&apos;t worry if you don&apos;t know
              them yet. Leave any (or all) blank and send them to us nearer the night.
            </p>
            {showAttendees && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Array.from({ length: totalSeats }).map((_, i) => (
                  <input key={i} value={attendees[i] || ''}
                    onChange={(e) => { const next = [...attendees]; next[i] = e.target.value; setAttendees(next); }}
                    data-testid={`ticket-attendee-${i}`}
                    placeholder={`Seat ${i + 1} — to be confirmed`}
                    className="border rounded-xl px-3 py-2 text-sm"/>
                ))}
              </div>
            )}
          </div>
          <label className="block">
            <span className="label">Dietary requirements or notes (optional)</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ticket-checkout-notes-input"
              className="mt-1 w-full border rounded-xl px-3 py-2" placeholder="e.g. 2× vegetarian, 1× nut allergy"/>
          </label>
          <div className="flex items-center justify-between pt-2 border-t border-charcoal/10">
            <div>
              <div className="text-xs text-charcoal/60">Total (ex VAT)</div>
              <div className="font-display text-2xl">{money(total, ticket.currency)}</div>
              <div className="text-xs text-charcoal/60" data-testid="ticket-total-inc-vat">
                {money(Math.round(total * 1.2 * 100) / 100, ticket.currency)} inc. 20% VAT at checkout
              </div>
            </div>
            <button data-testid="ticket-checkout-submit-btn" onClick={checkout} disabled={submitting}
              className="bg-heritage text-base font-semibold rounded-full px-6 py-3 flex items-center gap-2 hover:bg-charcoal transition-colors">
              {submitting ? <><Loader2 className="animate-spin" size={16}/> Redirecting…</> : 'Pay with Stripe'}
            </button>
          </div>
          <div className="text-xs text-charcoal/50">
            You&apos;ll be redirected to Stripe to complete payment securely. We accept card and Apple/Google Pay.
            <br/>
            <span data-testid="ticket-refund-policy">Awards tickets are <strong>non-refundable</strong>. In the event of an unavoidable situation, we will attempt to resell your tickets — if successful, we&apos;ll refund the amount paid less a <strong>20% administration cost</strong>.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sign-in / apply panel shown below tickets when unsigned. Two tabs:
 *  - Sign in: existing trade-partner accounts (via /api/partners/login)
 *  - Apply: quick 3-field application (via /api/partners/apply) — admin approves,
 *    partner receives set-password email.
 * Successful sign-in updates the parent's `partner` state so the tickets grid
 * immediately re-renders with tier-appropriate pricing.
 */
function SignInOrApplyPanel({ onSignIn }) {
  const [tab, setTab] = useState('signin');
  return (
    <section id="signin-panel" className="bg-stone border-t border-charcoal/10 py-16" data-testid="awards-signin-panel">
      <div className="max-w-[560px] mx-auto px-6">
        <div className="text-center mb-8">
          <div className="label text-heritage mb-3">Trade partner login required</div>
          <h2 className="font-display text-3xl mb-2">Almost there</h2>
          <p className="text-charcoal/70">
            Awards tickets are sold to our trade partner community. If you have an account, sign in;
            if you don&apos;t, apply for access below — it&apos;s free and takes a minute.
          </p>
        </div>
        <div className="flex border-b border-charcoal/15 mb-6" role="tablist">
          <TabButton active={tab === 'signin'} onClick={() => setTab('signin')} testid="awards-signin-tab">
            <LogIn size={14}/> Sign in
          </TabButton>
          <TabButton active={tab === 'apply'} onClick={() => setTab('apply')} testid="awards-apply-tab">
            <UserPlus size={14}/> Create trade partner login
          </TabButton>
        </div>
        {tab === 'signin'
          ? <MiniSignInForm onSignIn={onSignIn}/>
          : <MiniApplyForm onSwitchToSignIn={() => setTab('signin')}/>}
      </div>
    </section>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick} data-testid={testid}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
        active ? 'border-heritage text-heritage' : 'border-transparent text-charcoal/55 hover:text-charcoal'
      }`}>
      {children}
    </button>
  );
}

function MiniSignInForm({ onSignIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const { data } = await partnerApi.post('/partners/login', { email, password });
      setPartnerSession({ token: data.token, partner: data.partner });
      // Immediately fetch /me so we get the freshly-shaped record (with membership_tier)
      const { data: me } = await partnerApi.get('/partners/me');
      onSignIn(me);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Invalid email or password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mini-signin-form">
      <MiniField label="Email" type="email" value={email} onChange={setEmail} testid="mini-signin-email" required/>
      <MiniField label="Password" type="password" value={password} onChange={setPassword} testid="mini-signin-password" required/>
      {err && <div className="text-sm text-red-700" data-testid="mini-signin-error">{err}</div>}
      <button type="submit" disabled={busy} data-testid="mini-signin-submit"
        className="w-full bg-heritage text-base font-semibold rounded-full py-3 hover:bg-charcoal disabled:opacity-50 transition-colors">
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <p className="text-xs text-charcoal/55 text-center">
        Forgot your password? <a href="/partners" className="text-heritage underline">Reset it here</a>.
      </p>
    </form>
  );
}

function MiniApplyForm({ onSwitchToSignIn }) {
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await partnerApi.post('/partners/apply', {
        email, business_name: businessName, contact_name: contactName,
      });
      setSent(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Sorry, something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="text-center py-6" data-testid="mini-apply-sent">
        <Check size={36} className="text-heritage mx-auto mb-3"/>
        <h3 className="font-display text-2xl mb-2">Thanks — application received</h3>
        <p className="text-charcoal/70 mb-4">
          Our partnerships team will review your details and email you a password-set link within a working day or two.
          Once you&apos;re set up, come back here to book your tickets.
        </p>
        <button type="button" onClick={onSwitchToSignIn} data-testid="mini-apply-back-to-signin"
          className="text-sm font-semibold text-heritage hover:underline">
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mini-apply-form">
      <div className="flex items-center gap-2 text-sm text-charcoal/70 mb-2">
        <UserPlus size={14} className="text-heritage"/> Quick sign-up — no payment or commitment, just a login to buy tickets.
      </div>
      <MiniField label="Your name" value={contactName} onChange={setContactName} testid="mini-apply-contact-name" required/>
      <MiniField label="Business name" value={businessName} onChange={setBusinessName} testid="mini-apply-business-name" required/>
      <MiniField label="Email" type="email" value={email} onChange={setEmail} testid="mini-apply-email" required/>
      {err && <div className="text-sm text-red-700" data-testid="mini-apply-error">{err}</div>}
      <button type="submit" disabled={busy} data-testid="mini-apply-submit"
        className="w-full bg-heritage text-base font-semibold rounded-full py-3 hover:bg-charcoal disabled:opacity-50 transition-colors">
        {busy ? 'Sending…' : 'Create trade partner login'}
      </button>
      <p className="text-xs text-charcoal/55 text-center">
        A trade partner login is free — the discounted member rate applies once a paid Bronze, Silver or Gold package is attached to you or your venue.
      </p>
    </form>
  );
}

function MiniField({ label, value, onChange, type = 'text', testid, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">
        {label}{required && <span className="text-heritage ml-0.5">*</span>}
      </label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid}
        className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-heritage"/>
    </div>
  );
}
